import {
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AlertIcon, CheckIcon, ClockIcon, PauseIcon, XIcon } from './icons.js';

export function Button({
  variant = 'primary',
  busy = false,
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'link';
  busy?: boolean;
}) {
  const styles = {
    primary: 'bg-blue-700 text-white hover:bg-blue-800 disabled:bg-blue-400',
    secondary: 'border border-slate-300 bg-white text-slate-900 hover:bg-slate-100',
    danger: 'border border-red-300 bg-white text-red-800 hover:bg-red-50',
    link: 'text-blue-800 underline hover:text-blue-950 px-0 py-0',
  }[variant];
  return (
    <button
      type="button"
      {...props}
      disabled={props.disabled || busy}
      aria-busy={busy || undefined}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 py-2 text-base font-semibold ${styles} ${className}`}
    >
      {children}
    </button>
  );
}

interface FieldProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  optional?: boolean;
}

export function TextField({
  label,
  hint,
  error,
  optional,
  id,
  ...props
}: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const { t } = useTranslation();
  const auto = useId();
  const fieldId = id ?? auto;
  const describedBy =
    [hint ? `${fieldId}-hint` : null, error ? `${fieldId}-error` : null]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={fieldId} className="font-semibold">
        {label}
        {optional ? (
          <span className="font-normal text-slate-600"> ({t('app.optional')})</span>
        ) : null}
      </label>
      {hint ? (
        <p id={`${fieldId}-hint`} className="text-sm text-slate-700">
          {hint}
        </p>
      ) : null}
      <input
        id={fieldId}
        {...props}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={`min-h-11 rounded-md border px-3 py-2 text-base ${error ? 'border-red-700' : 'border-slate-400'} bg-white`}
      />
      {error ? (
        <p
          id={`${fieldId}-error`}
          className="flex items-center gap-1 text-sm font-medium text-red-800"
        >
          <AlertIcon />
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function SelectField({
  label,
  hint,
  error,
  id,
  children,
  ...props
}: FieldProps & SelectHTMLAttributes<HTMLSelectElement>) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={fieldId} className="font-semibold">
        {label}
      </label>
      {hint ? (
        <p id={`${fieldId}-hint`} className="text-sm text-slate-700">
          {hint}
        </p>
      ) : null}
      <select
        id={fieldId}
        {...props}
        aria-describedby={hint ? `${fieldId}-hint` : undefined}
        className="min-h-11 rounded-md border border-slate-400 bg-white px-3 py-2 text-base"
      >
        {children}
      </select>
      {error ? <p className="text-sm font-medium text-red-800">{error}</p> : null}
    </div>
  );
}

export function Checkbox({
  label,
  error,
  id,
  ...props
}: { label: ReactNode; error?: string | undefined } & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-3">
        <input
          id={fieldId}
          type="checkbox"
          {...props}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          className="mt-1 h-5 w-5 shrink-0 accent-blue-700"
        />
        <label htmlFor={fieldId}>{label}</label>
      </div>
      {error ? (
        <p id={`${fieldId}-error`} className="text-sm font-medium text-red-800">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Message box; errors are announced at once (role=alert), success politely (role=status). */
export function Notice({
  kind,
  children,
}: {
  kind: 'error' | 'success' | 'info' | 'warning';
  children: ReactNode;
}) {
  const styles = {
    error: 'border-red-300 bg-red-50 text-red-900',
    success: 'border-green-300 bg-green-50 text-green-900',
    info: 'border-blue-200 bg-blue-50 text-blue-950',
    warning: 'border-amber-300 bg-amber-50 text-amber-950',
  }[kind];
  const icon = kind === 'success' ? <CheckIcon /> : kind === 'info' ? <ClockIcon /> : <AlertIcon />;
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-md border px-4 py-3 ${styles}`}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div>{children}</div>
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={`rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6 ${className}`}
    >
      {children}
    </section>
  );
}

export function Loading() {
  const { t } = useTranslation();
  return (
    <p role="status" className="py-8 text-center text-slate-700">
      {t('app.loading')}
    </p>
  );
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Notice kind="error">
      <p>{message}</p>
      <Button variant="link" onClick={onRetry}>
        {t('app.retry')}
      </Button>
    </Notice>
  );
}

export type MonitorState = 'unknown' | 'up' | 'suspect' | 'down' | 'degraded' | 'paused';

/** State with icon and text, never colour alone. */
export function StatusBadge({ state }: { state: MonitorState }) {
  const { t } = useTranslation();
  const look = {
    up: ['bg-green-100 text-green-900', <CheckIcon key="i" />],
    down: ['bg-red-100 text-red-900', <XIcon key="i" />],
    suspect: ['bg-amber-100 text-amber-950', <ClockIcon key="i" />],
    degraded: ['bg-amber-100 text-amber-950', <AlertIcon key="i" />],
    paused: ['bg-slate-200 text-slate-900', <PauseIcon key="i" />],
    unknown: ['bg-slate-100 text-slate-800', <ClockIcon key="i" />],
  }[state] as [string, ReactNode];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-semibold ${look[0]}`}
    >
      {look[1]}
      {t(`status.${state}`)}
    </span>
  );
}

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export function SeverityBadge({ severity }: { severity: Severity }) {
  const { t } = useTranslation();
  const style = {
    critical: 'bg-red-100 text-red-900',
    high: 'bg-orange-100 text-orange-950',
    medium: 'bg-amber-100 text-amber-950',
    low: 'bg-slate-100 text-slate-900',
    info: 'bg-blue-50 text-blue-950',
  }[severity];
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-sm font-semibold ${style}`}>
      {t(`severity.${severity}`)}
    </span>
  );
}
